import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Patient } from '../types/patient.types';
import { PatientService } from '../services/patient.service';
import ViewAppointmentDialog from '../../appointments/components/ViewAppointmentDialog';
import AppointmentDialog from '../../appointments/components/AppointmentDialog';

const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = React.useState(0);
  const [appointments, setAppointments] = React.useState<any[]>([]);
  const [apptLoading, setApptLoading] = React.useState(false);

  // Fetch appointments for patient when tab is active
  React.useEffect(() => {
    if (activeTab !== 1 || !id) return;
    setApptLoading(true);
    import('../../appointments/api/appointmentApi').then(({ appointmentApi }) => {
      appointmentApi.getAll({ patientId: id, page: 1, limit: 5 })
        .then((result: any) => setAppointments(result.data || []))
        .catch(() => setAppointments([]))
        .finally(() => setApptLoading(false));
    });
  }, [activeTab, id]);
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  // Dialog state for viewing appointment details
  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  // Dialog state for adding new appointment
  const [addAppointmentDialogOpen, setAddAppointmentDialogOpen] = useState(false);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [loadingDoctorsLocations, setLoadingDoctorsLocations] = useState(false);

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await PatientService.getPatientById(id);
        setPatient(data);
      } catch (err) {
        setError('Failed to load patient data');
        console.error('Error fetching patient:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  const handleToggleStatus = async () => {
    if (!patient) return;
    
    try {
      setUpdatingStatus(true);
      const updatedPatient = await PatientService.togglePatientStatus(patient.id, !patient.isActive);
      setPatient(updatedPatient);
    } catch (err) {
      console.error('Error toggling patient status:', err);
      // Handle error (show toast)
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!patient) return;
    
    try {
      setDeleting(true);
      await PatientService.deletePatient(patient.id);
      navigate('/patient-management/patients');
    } catch (err) {
      console.error('Error deleting patient:', err);
      // Handle error (show toast)
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Box textAlign="center" my={4}>
        <Typography color="error">{error || 'Patient not found'}</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/patient-management/patients')}
          sx={{ mt: 2 }}
        >
          Back to Patients
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/patient-management/patients')}
        sx={{ mb: 2 }}
      >
        Back to Patients
      </Button>

      <Card>
        <CardHeader
          title="Patient Details"
          titleTypographyProps={{ variant: 'h5' }}
          action={
            <Box>
              <IconButton
                onClick={() => navigate(`/patient-management/patients/${patient.id}/edit`)}
                color="primary"
                title="Edit Patient"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={handleToggleStatus}
                color={patient.isActive ? 'success' : 'default'}
                disabled={updatingStatus}
                title={patient.isActive ? 'Deactivate Patient' : 'Activate Patient'}
              >
                {patient.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
              </IconButton>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                color="error"
                title="Delete Patient"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="Patient tabs">
              <Tab label="Details" />
              <Tab label="Appointments" />
            </Tabs>
          </Box>
          {activeTab === 0 && (
            <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                MRN
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.mrn}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <Box>
                <Chip
                  label={patient.isActive ? 'Active' : 'Inactive'}
                  color={patient.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Full Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.fullName}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Age
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.age} years
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Gender
              </Typography>
              <Typography variant="body1" gutterBottom>
                {patient.gender}
              </Typography>
            </Grid>

            {patient.phoneNumber && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Phone Number
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {patient.phoneNumber}
                </Typography>
              </Grid>
            )}

            {patient.email && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {patient.email}
                </Typography>
              </Grid>
            )}

            {patient.address && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Address
                </Typography>
                <Typography variant="body1" gutterBottom style={{ whiteSpace: 'pre-line' }}>
                  {patient.address}
                </Typography>
              </Grid>
            )}

            {patient.note && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Notes
                </Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {patient.note}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body2">
                {new Date(patient.createdAt).toLocaleString()}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {new Date(patient.updatedAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
          )}
          {activeTab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Appointments</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={async () => {
                    setAddAppointmentDialogOpen(true);
                    // Load doctors and locations
                    if (doctors.length === 0 || locations.length === 0) {
                      setLoadingDoctorsLocations(true);
                      try {
                        const [employeeApiModule, locationsApiModule] = await Promise.all([
                          import('../../settings/employees/api/employeeApi'),
                          import('../../settings/locations/api/locationsApi')
                        ]);
                        const [doctorsData, locationsData] = await Promise.all([
                          employeeApiModule.employeeApi.getAll({ employeeType: 'DOCTOR', limit: 100 }),
                          locationsApiModule.locationsApi.getAll({ limit: 100 })
                        ]);
                        setDoctors(doctorsData.data || []);
                        setLocations(locationsData.data || []);
                      } catch (error) {
                        console.error('Error loading doctors/locations:', error);
                      } finally {
                        setLoadingDoctorsLocations(false);
                      }
                    }
                  }}
                >
                  Add Appointment
                </Button>
              </Box>
              {apptLoading ? (
                <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>
              ) : appointments.length === 0 ? (
                <Typography>No appointments found for this patient.</Typography>
              ) : (
                <Box>
                  <Table sx={{ width: '100%', mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Doctor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>View Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appointments.map((appt: any, idx: number) => (
                        <TableRow
                          key={appt.id}
                          hover
                          sx={{ backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.100' }}
                        >
                          <TableCell>{appt.date || appt.appointmentDate}</TableCell>
                          <TableCell>{appt.time}</TableCell>
                          <TableCell>{appt.doctorName}</TableCell>
                          <TableCell>{appt.status}</TableCell>
                          <TableCell>{appt.type}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setSelectedAppointment(appt);
                                setViewDialogOpen(true);
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {appointments.length >= 5 && (
                    <Button
                      variant="outlined"
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/appointments?patientId=${patient.id}`)}
                    >
                      View All Appointments
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Patient</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {patient.fullName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <ViewAppointmentDialog
        open={viewDialogOpen}
        appointment={selectedAppointment}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedAppointment(null);
        }}
      />
      
      {/* Add Appointment Dialog */}
      {patient && (
        <AppointmentDialog
          open={addAppointmentDialogOpen}
          onClose={() => setAddAppointmentDialogOpen(false)}
          mode="create"
          doctors={doctors}
          locations={locations}
          loading={loadingDoctorsLocations}
          appointment={{
            patientId: patient.id,
            patient: {
              id: patient.id,
              mrn: patient.mrn,
              fullName: patient.fullName,
              age: patient.age,
              gender: patient.gender,
              phoneNumber: patient.phoneNumber || '',
              email: patient.email || '',
              address: patient.address || '',
              note: patient.note,
              isActive: patient.isActive
            },
            startTime: new Date().toISOString(),
            endTime: new Date().toISOString(),
          } as any}
          onSave={async (appointment) => {
            // Import appointmentApi dynamically
            const { appointmentApi } = await import('../../appointments/api/appointmentApi');
            await appointmentApi.create({
              ...appointment,
              patientId: patient.id
            } as any);
            setAddAppointmentDialogOpen(false);
            // Refresh appointments list if on appointments tab
            if (activeTab === 1) {
              setApptLoading(true);
              appointmentApi.getAll({ patientId: patient.id, page: 1, limit: 5 })
                .then((result: any) => setAppointments(result.data || []))
                .catch(() => setAppointments([]))
                .finally(() => setApptLoading(false));
            }
          }}
          onRefresh={() => {
            // Refresh appointments list
            if (activeTab === 1 && patient?.id) {
              setApptLoading(true);
              import('../../appointments/api/appointmentApi').then(({ appointmentApi }) => {
                appointmentApi.getAll({ patientId: patient.id, page: 1, limit: 5 })
                  .then((result: any) => setAppointments(result.data || []))
                  .catch(() => setAppointments([]))
                  .finally(() => setApptLoading(false));
              });
            }
          }}
        />
      )}
    </Box>
  );
};

export default PatientDetailPage;
